import { supabase } from '../lib/supabase';

/**
 * Memory Reprocess Service
 * Asks the `memory-reprocess` edge function to rebuild a memory's embedding
 * and knowledge-graph links after it was created or edited in the dashboard.
 *
 * Runs in the background of a save, so it never throws: every failure
 * resolves to { ok: false, error }. The edit itself is already saved.
 * A 409 means another re-process of the same memory is running (two quick
 * saves); retry once after a short wait so the latest text wins.
 */
const post = async (memoryId, accessToken) =>
  fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/memory-reprocess`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ memory_id: memoryId }),
  });

export const reprocessMemory = async (memoryId, { retryDelayMs = 4000 } = {}) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { ok: false, error: 'Not signed in' };

    let res = await post(memoryId, session.access_token);
    if (res.status === 409) {
      await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      res = await post(memoryId, session.access_token);
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const error = body.error || `Re-processing failed (${res.status})`;
      console.error('Error re-processing memory:', error);
      return { ok: false, error };
    }
    return { ok: true, error: null };
  } catch (err) {
    console.error('Error re-processing memory:', err);
    return { ok: false, error: err.message };
  }
};
